/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
import React, {useEffect} from 'react';
import {useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import useSaveStatusPrompt from '@ion/applications/src/pages/admin/accounts/useSaveStatusPrompt';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  GenericObject
} from '@ion/core/src/types/entities';
import {EmissionTester} from '@ion/core/src/types/services';
import Alert, {Placement} from '@ion/dls/src/components/alert/Alert';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import TertiaryButton from '@ion/dls/src/components/buttons/TertiaryButton';
import {ButtonSpinner} from '@ion/dls/src/components/loading/ButtonSpinner';
import {CarbEditorFields, deserializeAddress} from '@ion/dls/src/editors/GroupEditorUtils';
import useFormSave from '@ion/dls/src/hooks/useFormSave';
import useFormState, {FormState} from '@ion/dls/src/hooks/useFormState';
import useSaveEmissionTester from '@ion/query/src/hooks/useSaveEmissionTester';
import {ComplianceFields} from './ComplianceFields';

export interface ComplianceFormProps {
  complianceValues: EmissionTester;
  setShowCompliance: Function;
  showCancel?: boolean;
  setShowCancel?: Function;
};

const useStyles = makeStyles(() => {
  return {
    complianceEditor : {
      padding : 20
    }
  };
});

/**
 * The ComplianceForm component displays an editable CARB Compliance form inside Alert component
 * for a single Emission Tester entry.
 * 
 * ```typescript
 * import {ComplianceForm} from '@ion/applications/src/bulk/carb/ComplianceForm';
 * 
 * const SomeComponent = () => {
 *   return (
 *     <ComplianceForm  complianceValues={complianceValues}
 *      ... />
 *   );
 * };
 * ```
 */
export const ComplianceForm = ({
  complianceValues,
  setShowCompliance,
  setShowCancel,
  showCancel = false
}: ComplianceFormProps) => {
  const classes                   = useStyles();
  const {globalGroup}             = useSelector(({globalGroup}) => globalGroup);
  const [formState, setFormState] = useFormState({...deserializeAddress(complianceValues, true)});

  const submitValidation = {
    [CarbEditorFields.COMPLIANCE_SPECIALIST_EMAIL] : ({value}) => {
      return Boolean((formState[CarbEditorFields.COMPLIANCE_SPECIALIST_EMAIL].value || value)?.length);
    },
    [CarbEditorFields.COMPLIANCE_SPECIALIST_ID] : ({value}) => {
      return Boolean((formState[CarbEditorFields.COMPLIANCE_SPECIALIST_ID].value || value)?.length);
    },
    [CarbEditorFields.COMPLIANCE_SPECIALIST_NAME] : ({value}) => {
      return Boolean((formState[CarbEditorFields.COMPLIANCE_SPECIALIST_NAME].value || value)?.length);
    }
  };

  const {
    update: saveEmissionTester,
    isSuccess: isSaveSuccess,
    isError: isSaveError,
    isLoading: isSaveLoading
  } = useSaveEmissionTester();

  useEffect(() => {
    if (isSaveSuccess) {
      setShowCompliance(false);
      setShowCancel?.(true);
    }
  }, [isSaveSuccess]);
  
  useSaveStatusPrompt(isSaveSuccess, isSaveError);
  
  const onUpdate = function(complianceFields: EmissionTester) {
    const testerInfo = {
      carbData : [{id: entityId(globalGroup), ...complianceFields}],
      isEdit   : !!complianceValues?.testerId
    };
    saveEmissionTester(testerInfo);
  };

  const onCancel = function() {
    setShowCompliance(false);
  };

  const serializer = (data: FormState): GenericObject => {
    const carbMetaData: GenericObject = {};

    const fieldMap: Record<string, string> = {
      [CarbEditorFields.COMPLIANCE_SPECIALIST_EMAIL] : CarbEditorFields.EMAIL_ADDRESS,
      [CarbEditorFields.COMPLIANCE_SPECIALIST_ID]    : CarbEditorFields.TESTER_ID,
      [CarbEditorFields.COMPLIANCE_SPECIALIST_NAME]  : CarbEditorFields.NAME
    };

    for (const [key, obj] of Object.entries(data)) {
      const mappedKey = fieldMap[key];

      if (mappedKey) {
        carbMetaData[mappedKey] = obj?.value ?? null;
      }
    }
    return carbMetaData;
  };
    
  const handleSubmit = useFormSave({
    validate  : submitValidation,
    serialize : serializer,
    onSave    : onUpdate,
    formState,
    setFormState
  });
  
  return (<div className={classes.complianceEditor}>
    <Alert type={'info'}
      placement={Placement.bottom}
      hideSecondaryAction={!showCancel}
      secondaryAction={<TertiaryButton onClick={onCancel}>{translate('common:editors.shared.cancel')}</TertiaryButton>}
      action={
        <PrimaryButton onClick={handleSubmit}>
          {isSaveLoading
            ? <ButtonSpinner />
            : <>{translate('common:editors:shared.update')}</>}
        </PrimaryButton>}
      title={translate('admin-groups:carb.infoTitle')}
      message={translate('admin-groups:carb.infoCarbMsg')}
      form={<ComplianceFields isInfoForm
        formState={formState}
        setFormState={setFormState}
        complianceValues={complianceValues} />} />
  </div>);
};

export default ComplianceForm;
